import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import api from '../lib/api'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import {
  Link2,
  Plus,
  Copy,
  Trash2,
  Pause,
  Play,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronRight,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings,
  Zap,
  RotateCcw,
  Mic,
  SkipForward
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PageHeader } from '@/components/dashboard-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import { onSocketEvent } from '@/hooks/useWebSocket'
import { WebhookSetupWizard, PlatformSetupGuides } from '@/components/webhooks'

type SetupMode = 'configuring' | 'listening' | 'active' | 'paused'  // 'listening' kept for backward compat

interface FormConnection {
  id: string
  name: string
  description?: string
  webhookUrl: string
  phoneField: string
  nameField: string
  emailField: string
  messageField: string
  delaySeconds: number
  includeContext: boolean
  status: 'active' | 'paused'
  callCount: number
  lastTriggeredAt?: string
  createdAt: string
  updatedAt?: string
  // Discovery fields
  setupMode: SetupMode
  samplePayload?: any
  discoveredFields?: any[]
  fieldMappings?: Record<string, string>
  fieldLabels?: Record<string, string>
  listeningStartedAt?: string
  sampleCapturedAt?: string
  // Assistant info
  voiceAssistantId?: string
  assistantName?: string
  assistantVoiceId?: string
}

interface ConnectionLog {
  id: string
  phoneNumber: string
  leadName?: string
  leadEmail?: string
  status: 'pending' | 'initiated' | 'completed' | 'failed' | 'skipped' | 'scheduled'
  callStatus?: string
  callDuration?: number
  errorMessage?: string
  payload: any
  createdAt: string
}

export default function FormConnectionsPage() {
  const brand = useBrand()
  const brandBaseName = splitBrandName(brand.appName)[0]
  const [connections, setConnections] = useState<FormConnection[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showWizard, setShowWizard] = useState(false)
  const [wizardConnection, setWizardConnection] = useState<FormConnection | null>(null)
  const [showLogsDialog, setShowLogsDialog] = useState(false)
  const [selectedConnection, setSelectedConnection] = useState<FormConnection | null>(null)
  const [logs, setLogs] = useState<ConnectionLog[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [expandedConnection, setExpandedConnection] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<{ title: string; description: string; variant?: 'destructive' | 'default'; onConfirm: () => void } | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    loadConnections()

    // Listen for real-time sample capture notifications (toast handled in loadConnections)
    const unsubscribe = onSocketEvent('webhook:sample-captured', () => {
      loadConnections()
    })

    return () => { unsubscribe() }
  }, [])

  // Poll for updates when any webhook is awaiting a submission (WebSocket fallback)
  useEffect(() => {
    const hasConfiguring = connections.some(
      c => (c.setupMode === 'configuring' || c.setupMode === 'listening') && !c.samplePayload
    )
    if (!hasConfiguring) return

    const interval = setInterval(() => {
      loadConnections()
    }, 5000)

    return () => clearInterval(interval)
  }, [connections])

  const loadConnections = async () => {
    try {
      const response = await api.get('/api/lead-webhooks')
      if (response.data.success) {
        const newWebhooks: FormConnection[] = response.data.webhooks

        // Detect newly captured samples (poll-based fallback for WebSocket)
        setConnections(prev => {
          for (const wh of newWebhooks) {
            const old = prev.find(c => c.id === wh.id)
            if (old && !old.samplePayload && wh.samplePayload) {
              const fieldCount = wh.discoveredFields?.length || 0
              toast({
                title: 'Form Submission Received!',
                description: `${wh.name} captured ${fieldCount} fields. Click Continue Setup to map them.`
              })
            }
          }
          return newWebhooks
        })
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load form connections',
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenWizard = (connection?: FormConnection) => {
    setWizardConnection(connection || null)
    setShowWizard(true)
  }

  const handleWizardComplete = () => {
    loadConnections()
  }

  const handleResetConnection = (connection: FormConnection) => {
    setConfirmAction({
      title: 'Reset Connection',
      description: `Reset "${connection.name}" to reconfigure field mappings? This will require a new test submission.`,
      onConfirm: async () => {
        try {
          await api.put(`/api/lead-webhooks/${connection.id}/reset`)
          toast({ title: 'Connection Reset', description: 'Form connection is ready for reconfiguration' })
          loadConnections()
        } catch (error: any) {
          toast({ title: 'Error', description: error.response?.data?.message || 'Failed to reset connection', variant: 'destructive' })
        }
      }
    })
  }

  const handleToggleStatus = async (connection: FormConnection) => {
    const newStatus = connection.status === 'active' ? 'paused' : 'active'
    try {
      await api.put(`/api/lead-webhooks/${connection.id}`, { status: newStatus })
      setConnections(connections.map(c =>
        c.id === connection.id ? { ...c, status: newStatus } : c
      ))
      toast({
        title: newStatus === 'active' ? 'Connection Activated' : 'Connection Paused',
        description: `${connection.name} is now ${newStatus}`
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update connection',
        variant: 'destructive'
      })
    }
  }

  const handleDelete = (connection: FormConnection) => {
    setConfirmAction({
      title: 'Delete Connection',
      description: `Are you sure you want to delete "${connection.name}"? This cannot be undone.`,
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await api.delete(`/api/lead-webhooks/${connection.id}`)
          setConnections(connections.filter(c => c.id !== connection.id))
          toast({ title: 'Connection Deleted', description: `${connection.name} has been removed` })
        } catch (error: any) {
          toast({ title: 'Error', description: error.response?.data?.message || 'Failed to delete connection', variant: 'destructive' })
        }
      }
    })
  }

  const handleRegenerateLink = (connection: FormConnection) => {
    setConfirmAction({
      title: 'Regenerate Link',
      description: 'Are you sure you want to regenerate the connection link? Your existing form integrations will stop working.',
      onConfirm: async () => {

    try {
      const response = await api.post(`/api/lead-webhooks/${connection.id}/regenerate-key`)
      if (response.data.success) {
        toast({
          title: 'Link Regenerated',
          description: 'Update your form integrations with the new link'
        })
        loadConnections()
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to regenerate link',
        variant: 'destructive'
      })
    }
      }
    })
  }

  const handleViewLogs = async (connection: FormConnection) => {
    setSelectedConnection(connection)
    setShowLogsDialog(true)
    setLogsLoading(true)

    try {
      const response = await api.get(`/api/lead-webhooks/${connection.id}/logs`)
      if (response.data.success) {
        setLogs(response.data.logs)
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load logs',
        variant: 'destructive'
      })
    } finally {
      setLogsLoading(false)
    }
  }

  const handleTestSubmission = async (connection: FormConnection) => {
    try {
      toast({
        title: 'Sending test...',
        description: 'Sending a test submission to your webhook'
      })
      const response = await api.post(`/api/lead-webhooks/${connection.id}/test-submission`)
      if (response.data.success) {
        toast({
          title: 'Pipeline Verified!',
          description: 'The connection is working. Now submit your real form to capture its actual fields.'
        })
        loadConnections()
      }
    } catch (error: any) {
      toast({
        title: 'Test Failed',
        description: error.response?.data?.message || 'Could not send test submission',
        variant: 'destructive'
      })
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({
        title: 'Copied',
        description: 'Connection link copied to clipboard'
      })
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Could not copy to clipboard. Please select and copy manually.',
        variant: 'destructive'
      })
    }
  }

  const getSetupModeBadge = (connection: FormConnection) => {
    const mode = connection.setupMode || 'active'
    switch (mode) {
      case 'configuring':
      case 'listening':
        // Split configuring into two sub-states
        if (connection.samplePayload) {
          return (
            <Badge className="bg-yellow-500/10 dark:bg-yellow-500/20 text-yellow-500 border-yellow-500/20">
              <Settings className="h-3 w-3 mr-1" />
              Setup Required
            </Badge>
          )
        }
        return (
          <Badge className="bg-blue-500/10 dark:bg-blue-500/20 text-blue-500 border-blue-500/20">
            <Clock className="h-3 w-3 mr-1" />
            Awaiting First Submission
          </Badge>
        )
      case 'active':
        return (
          <Badge className="bg-green-500/10 dark:bg-green-500/20 text-green-500 border-green-500/20">
            <Zap className="h-3 w-3 mr-1" />
            Active
          </Badge>
        )
      case 'paused':
        return (
          <Badge variant="secondary">
            <Pause className="h-3 w-3 mr-1" />
            Paused
          </Badge>
        )
      default:
        return <Badge variant="outline">{mode}</Badge>
    }
  }

  const getLogStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'initiated':
        return <Phone className="h-4 w-4 text-blue-500" />
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />
      case 'skipped':
        return <SkipForward className="h-4 w-4 text-muted-foreground" />
      case 'scheduled':
        return <Clock className="h-4 w-4 text-orange-500" />
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="grid gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageHeader
        eyebrow="Lead capture"
        title="Form connections"
        subtitle={`When visitors fill out your forms, ${brandBaseName} calls them automatically.`}
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <Link2 className="h-5 w-5 text-brand-royal" />
          </div>
        }
        actions={
          <Button onClick={() => handleOpenWizard()} className="brand-gradient text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20">
            <Plus className="mr-2 h-4 w-4" />
            Connect a form
          </Button>
        }
      />

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Link2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">How Form Connections Work</h3>
              <p className="text-sm text-muted-foreground mt-1">
                When a visitor submits a form on your website, the data is sent to {brandBaseName}.
                Within seconds, an AI voice call is initiated to follow up and schedule a consultation.
              </p>
              <div className="flex gap-4 mt-3 text-sm">
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" /> Works with Gravity Forms
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" /> Works with Zapier
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" /> Works with any form
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform Setup Guides */}
      <PlatformSetupGuides
        webhookUrl={connections.length > 0 ? connections[0].webhookUrl : undefined}
      />

      {/* Connections List */}
      {connections.length === 0 ? (
        <Card className="card-inset">
          <CardContent className="py-16 text-center">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Link2 className="h-8 w-8 text-primary" />
                </div>
                <div className="absolute -right-2 -bottom-2 h-8 w-8 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Phone className="h-4 w-4 text-green-500" />
                </div>
              </div>
            </div>
            <h3 className="text-xl font-semibold">Let's connect {brandBaseName} to your contact forms!</h3>
            <p className="text-muted-foreground mt-2 mb-6 max-w-md mx-auto">
              When visitors fill out your forms, {brandBaseName} will call them within seconds to schedule a consultation.
            </p>
            <Button size="lg" onClick={() => handleOpenWizard()}>
              <Plus className="mr-2 h-4 w-4" />
              Connect Your First Form
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {connections.map((connection) => (
            <Collapsible
              key={connection.id}
              open={expandedConnection === connection.id}
              onOpenChange={(open) => setExpandedConnection(open ? connection.id : null)}
            >
              <Card className="card-inset">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="p-0 h-auto">
                          {expandedConnection === connection.id ? (
                            <ChevronDown className="h-5 w-5" />
                          ) : (
                            <ChevronRight className="h-5 w-5" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg">{connection.name}</CardTitle>
                          {getSetupModeBadge(connection)}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {connection.description && (
                            <CardDescription>
                              {connection.description}
                            </CardDescription>
                          )}
                          {connection.assistantName && (
                            <Badge variant="outline" className="text-xs">
                              <Mic className="h-3 w-3 mr-1" />
                              {connection.assistantName}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {connection.setupMode === 'active' && (
                        <span className="text-sm text-muted-foreground">
                          {connection.callCount} calls
                        </span>
                      )}

                      {/* Configuring — different buttons for awaiting vs setup required */}
                      {(connection.setupMode === 'configuring' || connection.setupMode === 'listening') && (
                        connection.samplePayload ? (
                          <Button
                            size="sm"
                            onClick={() => handleOpenWizard(connection)}
                          >
                            <Settings className="mr-2 h-4 w-4" />
                            Continue Setup
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenWizard(connection)}
                            >
                              <Link2 className="mr-2 h-4 w-4" />
                              View Link
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestSubmission(connection)}
                              title="Send a test submission to verify the pipeline"
                            >
                              <Zap className="mr-2 h-4 w-4" />
                              Send Test
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => loadConnections()}
                              title="Check for new form submissions"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </>
                        )
                      )}

                      {/* Active connection actions */}
                      {connection.setupMode === 'active' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewLogs(connection)}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Logs
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleStatus(connection)}
                          >
                            {connection.status === 'active' ? (
                              <>
                                <Pause className="mr-2 h-4 w-4" />
                                Pause
                              </>
                            ) : (
                              <>
                                <Play className="mr-2 h-4 w-4" />
                                Activate
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResetConnection(connection)}
                            title="Reconfigure field mappings"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        </>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(connection)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CollapsibleContent>
                  <CardContent className="pt-0 border-t">
                    <div className="grid gap-4 mt-4">
                      {/* Connection Link */}
                      <div className="space-y-2">
                        <Label>Your Connection Link</Label>
                        <div className="flex gap-2">
                          <Input
                            value={connection.webhookUrl}
                            readOnly
                            className="font-mono text-sm"
                          />
                          <Button
                            variant="outline"
                            onClick={() => copyToClipboard(connection.webhookUrl)}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Link
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleRegenerateLink(connection)}
                            title="Regenerate link"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Add this link to your form builder's webhook or integration settings
                        </p>
                      </div>

                      {/* Field Mappings */}
                      {connection.fieldMappings && Object.keys(connection.fieldMappings).length > 0 ? (
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Matched Form Fields</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {Object.entries(connection.fieldMappings).map(([target, source]) => {
                              if (!source) return null
                              // Use stored label for context fields, otherwise humanize the key
                              const rawKey = target.replace('lead.', '').replace('context.', '')
                              const displayLabel = target.startsWith('context.')
                                ? (connection.fieldLabels?.[rawKey] || rawKey.replace(/_/g, ' '))
                                : rawKey
                              return (
                                <div key={target} className="flex items-center gap-2 text-sm">
                                  <Badge variant="outline" className="font-normal capitalize">
                                    {displayLabel}
                                  </Badge>
                                  <span className="text-muted-foreground">&rarr;</span>
                                  <code className="text-xs bg-muted px-1 rounded truncate">
                                    {typeof source === 'string' ? source : JSON.stringify(source)}
                                  </code>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-muted-foreground">Phone Field</Label>
                            <p className="font-mono text-sm">{connection.phoneField}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground">Name Field</Label>
                            <p className="font-mono text-sm">{connection.nameField}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground">Email Field</Label>
                            <p className="font-mono text-sm">{connection.emailField}</p>
                          </div>
                          <div>
                            <Label className="text-muted-foreground">Message Field</Label>
                            <p className="font-mono text-sm">{connection.messageField}</p>
                          </div>
                        </div>
                      )}

                      {/* Settings */}
                      <div className="flex items-center gap-6 pt-2">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {connection.delaySeconds > 0
                              ? `${connection.delaySeconds}s delay before call`
                              : 'Immediate call'}
                          </span>
                        </div>
                        {connection.lastTriggeredAt && (
                          <div className="text-sm text-muted-foreground">
                            Last triggered: {format(new Date(connection.lastTriggeredAt), 'MMM d, yyyy h:mm a')}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      {/* Connection Setup Wizard */}
      <WebhookSetupWizard
        open={showWizard}
        onClose={() => {
          setShowWizard(false)
          setWizardConnection(null)
        }}
        onComplete={handleWizardComplete}
        existingWebhook={wizardConnection || undefined}
      />

      {/* Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Call History: {selectedConnection?.name}</DialogTitle>
                <DialogDescription>
                  Recent form submissions and call status
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectedConnection && handleViewLogs(selectedConnection)}
                disabled={logsLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${logsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </DialogHeader>

          <div className="overflow-auto max-h-[500px]">
            {logsLoading ? (
              <div className="py-12 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">Loading history...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="py-12 text-center">
                <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground" />
                <p className="mt-2 text-muted-foreground">No form submissions yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Lead Name</TableHead>
                    <TableHead>Call Status</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getLogStatusIcon(log.status)}
                          <div>
                            <span className="capitalize">{log.status}</span>
                            {log.errorMessage && (
                              <p className="text-xs text-muted-foreground mt-0.5">{log.errorMessage}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {log.phoneNumber}
                      </TableCell>
                      <TableCell>
                        {log.leadName || '-'}
                      </TableCell>
                      <TableCell>
                        {log.callStatus ? (
                          <Badge variant={log.callStatus === 'completed' ? 'default' : 'secondary'}>
                            {log.callStatus}
                            {log.callDuration && ` (${log.callDuration}s)`}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {log.createdAt ? format(new Date(log.createdAt), 'MMM d, yyyy h:mm a') : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setShowLogsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog (replaces browser confirm()) */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmAction?.title}</DialogTitle>
            <DialogDescription>{confirmAction?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button
              variant={confirmAction?.variant === 'destructive' ? 'destructive' : 'default'}
              onClick={() => {
                confirmAction?.onConfirm()
                setConfirmAction(null)
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
